/**
 * Build internal-page URLs that respect the deploy base path.
 *
 * The frontend can be served either from the root of an origin (`/`)
 * or from a sub-path (e.g. `/uma/`). Vite's HTML transform handles
 * asset URLs in built HTML/JS when `--base=/uma/` is passed at build
 * time, but it does NOT rewrite string literals like
 * `"/landing.html"` that appear in source code. This helper closes
 * that gap.
 *
 * The base is provided at *build time* via `__APP_BASE__`, a global
 * string injected by `vite.config.js`'s `define` option (driven by
 * the `MLIPVIEW_BASE` env var). Using a baked-in constant is more
 * reliable than runtime detection from `document.baseURI`, because
 * some CDN/bucket setups route a path like `/uma` (no trailing
 * slash) to the directory's contents — `document.baseURI` then
 * resolves to `/` and runtime detection produces wrong URLs even
 * though the page IS being served under the sub-path.
 *
 * Outside of a Vite build (e.g. Jest), `__APP_BASE__` is undefined
 * and we fall back to `/`.
 *
 * Examples (__APP_BASE__='/uma/'):
 *   withBase('/landing.html')       → '/uma/landing.html'
 *   withBase('landing.html')        → '/uma/landing.html'
 *   withBase('/index.html?embed=1') → '/uma/index.html?embed=1'
 *   withBase('https://x.com/foo')   → 'https://x.com/foo' (untouched)
 */

declare const __APP_BASE__: string;

export function joinBase(base: string, path: string): string {
  // Absolute URLs (scheme://… or protocol-relative //…) are returned
  // unchanged — callers don't want their off-site links rewritten.
  if (/^[a-z][a-z0-9+\-.]*:\/\//i.test(path) || path.startsWith('//')) {
    return path;
  }
  const trimmedBase = base.replace(/\/+$/, '');
  const tail = path.startsWith('/') ? path : '/' + path;
  return trimmedBase + tail;
}

/**
 * Returns the deploy base path (`/` or `/uma/` etc.) baked in at
 * build time. Falls back to `/` outside of a Vite build.
 *
 * Uses `typeof` so that, when `__APP_BASE__` was not replaced (Jest
 * runtime), the identifier check is safe (typeof on an undeclared
 * identifier returns 'undefined' rather than throwing).
 */
export function detectBase(): string {
  if (typeof __APP_BASE__ !== 'undefined') return __APP_BASE__;
  return '/';
}

export function withBase(path: string): string {
  return joinBase(detectBase(), path);
}
